import React from 'react';
import { cn } from '../../../utils/cn';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const QuickActionCards = ({ userData, onSectionChange }) => {
  const quickActions = [
    {
      title: 'Recent Orders',
      description: 'View your latest purchases',
      value: userData?.totalOrders || 0,
      label: 'Total Orders',
      icon: 'Package',
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600',
      action: () => onSectionChange('orders')
    },
    {
      title: 'Address Book',
      description: 'Manage shipping addresses',
      value: '2',
      label: 'Saved Addresses',
      icon: 'MapPin',
      color: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600',
      action: () => onSectionChange('addresses')
    },
    {
      title: 'Payment Methods',
      description: 'Update payment info',
      value: '2',
      label: 'Saved Cards',
      icon: 'CreditCard',
      color: 'bg-purple-50 border-purple-200',
      iconColor: 'text-purple-600',
      action: () => onSectionChange('payments')
    },
    {
      title: 'Loyalty Points',
      description: 'Check your rewards',
      value: userData?.loyaltyPoints?.toLocaleString() || '0',
      label: 'Available Points',
      icon: 'Star',
      color: 'bg-amber-50 border-amber-200',
      iconColor: 'text-amber-600',
      action: () => onSectionChange('preferences')
    }
  ];

  const recentActivity = [];  // No mock — empty until a real activity feed exists.

  return (
    <div className="space-y-8 w-full overflow-hidden">
      {/* Quick Action Cards */}
      <div className="w-full">
        <h3 className="text-lg font-medium text-foreground mb-4">
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 w-full">
          {quickActions?.map((action, index) => (
            <button
              key={index}
              onClick={action?.action}
              className={cn(
                "p-4 rounded-lg border text-left transition-all hover:shadow-md hover:scale-105 w-full min-w-0 overflow-hidden cursor-pointer",
                action?.color
              )}
            >
              <div className="flex items-center justify-between mb-3 gap-3 min-w-0">
                <Icon 
                  name={action?.icon} 
                  size={24} 
                  className={cn(action?.iconColor, "flex-shrink-0")}
                />
                <div className="text-right flex-shrink-0">
                  <p className="text-xl sm:text-2xl font-bold text-foreground truncate">
                    {action?.value}
                  </p>
                  <p className="text-xs text-muted-foreground whitespace-nowrap truncate">
                    {action?.label}
                  </p>
                </div>
              </div>
              <h4 className="font-medium text-foreground mb-1 truncate">
                {action?.title}
              </h4>
              <p className="text-sm text-muted-foreground line-clamp-2 break-words">
                {action?.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Account Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full">
        {/* Account Status */}
        <div className="bg-white rounded-lg border border-border p-6 min-w-0 overflow-hidden">
          <div className="flex items-center justify-between mb-4 gap-4">
            <h4 className="font-medium text-foreground truncate">Account Status</h4>
            <Icon name="User" size={20} className="text-primary flex-shrink-0" />
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 gap-4 min-w-0">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Member since</span>
              <span className="text-sm font-medium text-foreground truncate">
                {new Date(userData?.joinDate)?.toLocaleDateString('en-US', { 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 gap-4 min-w-0">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Total spent</span>
              <span className="text-sm font-medium text-foreground truncate">
                ${userData?.totalSpent?.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 gap-4 min-w-0">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Account tier</span>
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 whitespace-nowrap flex-shrink-0">
                <Icon name="Star" size={12} className="mr-1 flex-shrink-0" />
                Premium
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="w-full mt-4"
            onClick={() => onSectionChange('profile')}
          >
            <span className="truncate">View Profile Details</span>
          </Button>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg border border-border p-6 min-w-0 overflow-hidden">
          <div className="flex items-center justify-between mb-4 gap-4">
            <h4 className="font-medium text-foreground truncate">Recent Activity</h4>
            <Icon name="Activity" size={20} className="text-primary flex-shrink-0" />
          </div>
          
          <div className="space-y-4">
            {recentActivity?.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3 min-w-0">
                <div className="flex-shrink-0">
                  <Icon 
                    name={activity?.icon} 
                    size={16} 
                    className={activity?.iconColor}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {activity?.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2 break-words">
                    {activity?.description}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 whitespace-nowrap">
                    {activity?.time}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-4 text-primary"
            onClick={() => onSectionChange('orders')}
          >
            <span className="truncate">View All Activity</span>
          </Button>
        </div>
      </div>

      {/* Security Alert */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200 p-4 w-full min-w-0 overflow-hidden">
        <div className="flex items-start space-x-3 min-w-0">
          <Icon name="Shield" size={20} className="text-green-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-green-900 mb-1 break-words">
              Account Security Status: Good
            </h4>
            <p className="text-sm text-green-800 mb-3 break-words">
              Your account security score is 85/100. Consider enabling two-factor authentication for better protection.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSectionChange('security')}
              className="border-green-300 text-green-700 hover:bg-green-100"
            >
              <span className="truncate">Review Security Settings</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickActionCards;